import api from '@/services/ApiService';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';

const METADATA_FIELDS = '_id,interval,timeStart,timeEnd';

function selectDefaultReport(reports) {
  if (!reports || reports.length === 0) {
    return null;
  }

  const hourlyReports = reports.filter(
    (report) => report.interval === 'HOURLY',
  );

  const collection = hourlyReports.length > 0 ? hourlyReports : reports;

  return collection.reduce((latest, current) => {
    return new Date(current.timeStart) > new Date(latest.timeStart)
      ? current
      : latest;
  }, collection[0]);
}

export default function CallcenterLayout() {
  const [reports, setReports] = useState([]);
  const [report, setReportState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);

  const reportCache = useRef(new Map());
  const inflight = useRef(new Map());

  const loadFullReport = useCallback(async (id) => {
    if (!id) return null;
    if (reportCache.current.has(id)) return reportCache.current.get(id);
    if (inflight.current.has(id)) return inflight.current.get(id);

    const promise = api
      .get(`/report/${id}`)
      .then(({ data }) => {
        reportCache.current.set(id, data);
        inflight.current.delete(id);
        return data;
      })
      .catch((err) => {
        inflight.current.delete(id);
        throw err;
      });
    inflight.current.set(id, promise);
    return promise;
  }, []);

  const loadReportsFull = useCallback(
    async (ids) => {
      const unique = Array.from(new Set(ids.filter(Boolean)));
      const results = await Promise.all(unique.map((id) => loadFullReport(id)));
      return results.filter(Boolean);
    },
    [loadFullReport],
  );

  const setReport = useCallback(
    async (meta) => {
      if (!meta) {
        setReportState(null);
        return;
      }
      const cached = reportCache.current.get(meta._id);
      if (cached) {
        setReportState(cached);
        return;
      }
      setReportLoading(true);
      try {
        const full = await loadFullReport(meta._id);
        setReportState(full);
      } finally {
        setReportLoading(false);
      }
    },
    [loadFullReport],
  );

  useEffect(() => {
    const fetchReports = () =>
      api
        .get(`/report?fields=${METADATA_FIELDS}`)
        .then(async ({ data: reportsResponse }) => {
          const fetchedReports = reportsResponse.result || [];
          setReports(fetchedReports);
          const defaultMeta = selectDefaultReport(fetchedReports);
          if (defaultMeta) {
            setReportLoading(true);
            try {
              const full = await loadFullReport(defaultMeta._id);
              setReportState(full);
            } finally {
              setReportLoading(false);
            }
          }
        });

    fetchReports().finally(() => {
      setLoading(false);
    });
  }, [loadFullReport]);

  return (
    <Outlet
      context={{
        reports,
        report,
        setReport,
        loading,
        reportLoading,
        loadReportsFull,
      }}
    />
  );
}
